import { Component, Input, Output, EventEmitter, signal, computed, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ButtonDirective,
  FormModule,
  RowComponent,
  ColComponent,
  SpinnerComponent,
  ProgressComponent,
} from '@coreui/angular-pro';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, interval, takeUntil, take } from 'rxjs';
import {
  MfaMethod,
  MfaStep,
  MfaSendCodeEvent,
  MfaVerifyCodeEvent,
  MfaConfig,
} from './mfa-verification.interface';
import { EMAIL_PATTERN, PHONE_PATTERN } from '@shared/utils/validation-patterns';

@Component({
  selector: 'app-mfa-verification',
  templateUrl: './mfa-verification.component.html',
  styleUrls: ['./mfa-verification.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonDirective,
    FormModule,
    RowComponent,
    ColComponent,
    SpinnerComponent,
    ProgressComponent,
    TranslateModule,
  ],
})
export class MfaVerificationComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private destroy$ = new Subject<void>();
  private timerDestroy$ = new Subject<void>();

  // Inputs
  @Input() config: MfaConfig = {
    availableMethods: ['phone', 'email'],
    expirationTimeMinutes: 5,
  };
  @Input() set loading(value: boolean) {
    this._loading.set(value);
  }
  @Input() showHeader = true;
  @Input() showCancel = true;
  @Input() showBack = true;
  @Input() variant: 'modal' | 'page' = 'modal';
  @Input() startOnVerifyStep = false; // Start directly on verify step (for regular MFA login)
  @Input() codeSentMessage = ''; // Custom message for code sent
  @Input() requiresSourceInput = true; // Whether user must enter phone/email (false for regular MFA with pre-configured addresses)
  @Input() showMethodInVerifyStep = false; // Show method selection dropdown in verify step (for regular MFA)

  // Outputs
  @Output() sendCode = new EventEmitter<MfaSendCodeEvent>();
  @Output() verifyCode = new EventEmitter<MfaVerifyCodeEvent>();
  @Output() resendCode = new EventEmitter<MfaSendCodeEvent>();
  @Output() methodChanged = new EventEmitter<MfaSendCodeEvent>(); // Emitted when method changes in verify step
  @Output() cancelClick = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  // State
  currentStep = signal<MfaStep>('select');
  _loading = signal(false);
  selectedMethod = signal<MfaMethod>('phone');
  verificationSource = signal('');
  verificationCode = signal('');

  // Timer
  expirationTime = signal(0);
  remainingTime = signal(0);
  timerProgress = signal(100);

  // Resend cooldown (60 seconds)
  private readonly RESEND_COOLDOWN = 60;
  canResend = computed(() => {
    const elapsed = this.expirationTime() - this.remainingTime();
    return elapsed >= this.RESEND_COOLDOWN;
  });

  // Validation
  private emailPattern = EMAIL_PATTERN;
  private phonePattern = PHONE_PATTERN;

  isValidInput = computed(() => {
    // When source input is not required, always valid (method selection only)
    if (!this.requiresSourceInput) {
      return true;
    }

    const source = this.verificationSource().trim();
    if (!source) {
      return false;
    }

    if (this.selectedMethod() === 'email') {
      return this.emailPattern.test(source);
    }
    return this.phonePattern.test(source);
  });

  hasInvalidInput = computed(() => {
    // No invalid state when source input is not required
    if (!this.requiresSourceInput) {
      return false;
    }

    const source = this.verificationSource().trim();
    if (!source) return false;
    return !this.isValidInput();
  });

  ngOnInit(): void {
    // Initialize with config defaults
    if (this.config.defaultMethod) {
      this.selectedMethod.set(this.config.defaultMethod);
    } else if (this.config.availableMethods.length > 0) {
      this.selectedMethod.set(this.config.availableMethods[0]);
    }

    if (this.config.initialSource) {
      this.verificationSource.set(this.config.initialSource);
    }

    // Start directly on verify step if configured
    if (this.startOnVerifyStep) {
      this.currentStep.set('verify');
      const expirationSeconds = (this.config.expirationTimeMinutes || 5) * 60;
      this.startTimer(expirationSeconds);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timerDestroy$.next();
    this.timerDestroy$.complete();
  }

  getMethodLabel(method: MfaMethod): string {
    return method === 'phone'
      ? this.translate.instant('MFA_METHOD_PHONE')
      : this.translate.instant('MFA_METHOD_EMAIL');
  }

  getPlaceholder(): string {
    return this.selectedMethod() === 'phone'
      ? this.translate.instant('MFA_PHONE_PLACEHOLDER')
      : this.translate.instant('MFA_EMAIL_PLACEHOLDER');
  }

  onMethodChange(): void {
    this.verificationSource.set('');
  }

  // Handle method change in verify step (triggers resend to new method)
  onMethodChangeInVerifyStep(newMethod: MfaMethod): void {
    if (newMethod === this.selectedMethod()) {
      return;
    }

    this.selectedMethod.set(newMethod);
    this.verificationCode.set(''); // Clear the code input
    this.methodChanged.emit({
      method: newMethod,
      verificationSource: this.verificationSource(),
    });
  }

  onSendCode(): void {
    if (!this.isValidInput() || this._loading()) return;

    this.sendCode.emit({
      method: this.selectedMethod(),
      verificationSource: this.verificationSource(),
    });
  }

  onVerifyCode(): void {
    if (!this.verificationCode() || this._loading()) return;

    this.verifyCode.emit({
      method: this.selectedMethod(),
      verificationSource: this.verificationSource(),
      verificationCode: this.verificationCode(),
    });
  }

  onResendCode(): void {
    if (this._loading() || !this.canResend()) return;

    this.resendCode.emit({
      method: this.selectedMethod(),
      verificationSource: this.verificationSource(),
    });
  }

  onCancel(): void {
    this.cancelClick.emit();
  }

  onBack(): void {
    this.goBackToSelect();
    this.back.emit();
  }

  // Called by parent after successful send
  goToVerifyStep(expirationTimeMinutes?: number): void {
    const expTime = expirationTimeMinutes || this.config.expirationTimeMinutes || 5;
    const expirationSeconds = expTime * 60;
    this.startTimer(expirationSeconds);
    this.currentStep.set('verify');
    this.verificationCode.set('');
  }

  // Called by parent after successful resend
  restartTimer(expirationTimeMinutes?: number): void {
    const expTime = expirationTimeMinutes || this.config.expirationTimeMinutes || 5;
    const expirationSeconds = expTime * 60;
    this.startTimer(expirationSeconds);
  }

  goBackToSelect(): void {
    this.timerDestroy$.next();
    this.currentStep.set('select');
    this.verificationCode.set('');
    this.remainingTime.set(0);
    this.timerProgress.set(100);
  }

  getExpirationMinutes(): number | undefined {
    return this.config.expirationTimeMinutes;
  }

  private startTimer(durationSeconds: number): void {
    this.timerDestroy$.next();
    this.expirationTime.set(durationSeconds);
    this.remainingTime.set(durationSeconds);
    this.timerProgress.set(100);

    interval(1000)
      .pipe(
        take(durationSeconds),
        takeUntil(this.timerDestroy$),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          const newRemaining = this.remainingTime() - 1;
          this.remainingTime.set(newRemaining);
          this.timerProgress.set((newRemaining / durationSeconds) * 100);
        },
        complete: () => {
          this.remainingTime.set(0);
          this.timerProgress.set(0);
        },
      });
  }
}

